import * as React from "react"
import { cn } from "../../lib/utils"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { Button } from "./button"
import { XIcon } from "lucide-react"
import { useHelixRoot } from "../hx/hooks"

// The template's dialog (next-shadcn-admin-dashboard/src/components/ui/
// dialog.tsx at bff60d4), every class unchanged, ported by a script: Helix's
// Radix import and `cn`, a reduced-motion stop on each part that animates,
// the call-bar exception below, and a portal into the `.helix` root.
// Record: docs/testing/ui-migration/025-ui-dialog.md.

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

// Helix: the dialog renders inside the `.helix` root, not at the end of
// <body>. Every hx control rule is scoped to `.helix` (hx-controls.css,
// shell-helix-data.css), so an HxButton or HxInput inside a dialog on <body>
// drew unstyled (probe, pass 035). A caller's own `container` still wins.
function DialogPortal({
  container,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  const helix = useHelixRoot()
  return <DialogPrimitive.Portal data-slot="dialog-portal" container={container ?? helix} {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 motion-reduce:animate-none",
        className
      )}
      {...props}
    />
  )
}

// Helix: a click on the call bar (pages/voice/CallBar.tsx, data-callbar) is
// not a click "outside" the dialog. The bar stays usable during a call (it
// sets pointer-events: auto), and hanging up must not also throw away a
// half-filled form. Pass 025's record.
function keepOpenForCallBar(event: { target: EventTarget | null; preventDefault: () => void }) {
  const t = event.target as Element | null
  if (t && typeof t.closest === "function" && t.closest("[data-callbar]")) event.preventDefault()
}

type OverlayHandlers = Pick<
  React.ComponentProps<typeof DialogPrimitive.Content>,
  "onInteractOutside" | "onOpenAutoFocus" | "onCloseAutoFocus"
>

// Helix's two additions to a modal overlay's content (this dialog and
// sheet.tsx), wrapped around the caller's own handlers:
// - the call-bar exception above;
// - on close, focus goes back to whatever had it when the overlay opened.
//   Radix returns it to a Trigger, and Helix's overlays open from state with
//   none, so focus fell to <body> (probe, pass 035). A caller that prevents
//   the default keeps its own handling.
function useHelixOverlayHandlers({ onInteractOutside, onOpenAutoFocus, onCloseAutoFocus }: OverlayHandlers): Required<OverlayHandlers> {
  const restoreRef = React.useRef<HTMLElement | null>(null)
  return {
    onInteractOutside: (e) => {
      keepOpenForCallBar(e)
      onInteractOutside?.(e)
    },
    onOpenAutoFocus: (e) => {
      restoreRef.current = document.activeElement as HTMLElement | null
      onOpenAutoFocus?.(e)
    },
    onCloseAutoFocus: (e) => {
      onCloseAutoFocus?.(e)
      const el = restoreRef.current
      if (e.defaultPrevented || !el?.isConnected) return
      e.preventDefault()
      el.focus()
    },
  }
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onInteractOutside,
  onOpenAutoFocus,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  const helixHandlers = useHelixOverlayHandlers({ onInteractOutside, onOpenAutoFocus, onCloseAutoFocus })
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 motion-reduce:animate-none",
          className
        )}
        {...helixHandlers}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button
              variant="ghost"
              className="absolute top-2 right-2"
              size="icon-sm"
            >
              <XIcon
              />
              <span className="sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-heading text-base leading-none font-medium",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  useHelixOverlayHandlers,
}
